<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import Cookies from 'js-cookie'
import plural from 'plural-ru'
import { shuffle as _shuffle } from 'lodash-es'
import AudioPlayerV2 from '../AudioPlayerV2.vue'
import Cinderella from './Songs/Cinderella.vue'
import Molly from './Songs/Molly.vue'
import Snowwhite from './Songs/Snowwhite.vue'
import Magician from './Songs/Magician.vue'
import { useNavigation } from '../useNavigation'
import { AudioActivityTracker } from '@/helpers/trackers'
import type { Student2Context } from '@/types/student2'
import { useStaticAsset } from '@/composables/useStaticAsset'

const asset = useStaticAsset()

const props = defineProps<{ context: Student2Context }>()
const { getLink: $getLink, gotoOnClick: $gotoOnClick } = useNavigation()

const studentId = Number(window.location.pathname.split('/').slice(-2)[0])
const unitId = Number(window.location.pathname.split('/').slice(-1)[0])

const tailsAudioOrder = ref<string[]>([])
const currentAudioOrder = ref<string[]>([])
const unshuffledOrders = ref<{ current: string[]; tails: string[] }>({ current: [], tails: [] })

const shuffle = ref(false)
const repeat = ref(-1)
const playingAll = ref(false)
const mode = ref(0)

const audioPlayer = ref<Array<{ $el: HTMLElement; play: () => void }>>([])
const materialTrackers: Record<string, AudioActivityTracker> = {}

const pluralize = (value = 0, one: string, two: string, five: string) => `${value} ${plural(value, one, two, five)}`

const unitHasSongs = computed(() => props.context.songs.filter(m => m.unit_id === unitId).length)

const tailSongKey = (tail: Student2Context['student']['tails'][number]) => {
  const song = tail.custom_data?.song
  if (!song) return null
  return typeof song === 'string' ? song : song.key
}

const tailSongName = (tail: Student2Context['student']['tails'][number]) => {
  const song = tail.custom_data?.song
  if (!song) return null
  return typeof song === 'string' ? song : song.name
}

const materials = computed(() => {
  const currentMode = ['plus', 'minus'][mode.value]

  return {
    current: currentAudioOrder.value.flatMap(key => {
      const song = props.context.songs.find(e => e.key === key)
      if (!song) return []
      return {
        id: key,
        name: song.name,
        rus: {
          id: key,
          name: song.name,
          url: `/student/get_song_file/${studentId}/${unitId}/${key}/${currentMode}`
        }
      }
    }),

    tails: tailsAudioOrder.value.flatMap(key => {
      const tail = props.context.student.tails.find(e => tailSongKey(e) === key)
      const songName = tail ? tailSongName(tail) : null
      if (!tail || !songName || tail.unit_id == null) return []

      return {
        id: key,
        name: songName,
        rus: {
          id: key,
          name: songName,
          url: `/student/get_song_file/${studentId}/${tail.unit_id}/${key}/${currentMode}`
        }
      }
    })
  }
})

const playAll = () => {
  playingAll.value = true
  audioPlayer.value[0]?.play()
}

const changeRepeat = () => {
  if (repeat.value === 1) repeat.value = -1
  else repeat.value++
}

const onAudioPlay = (materialId: string, reason: 'tail' | 'current') => {
  const trackerKey = `${materialId}-${reason}`
  if (materialTrackers[trackerKey]) return

  const audioElement = document.querySelector(
    `div[data-material-id="${materialId}"] > audio`
  ) as HTMLAudioElement | null
  if (!audioElement) return

  materialTrackers[trackerKey] = new AudioActivityTracker(audioElement, (ms: number) => {
    const form = new FormData()
    form.append('csrfmiddlewaretoken', Cookies.get('csrftoken') ?? '')
    form.append('student_id', String(studentId))
    form.append('song_key', materialId)
    form.append('milliseconds', String(ms))

    navigator.sendBeacon('/student/track_song_activity', form)
  })

  materialTrackers[trackerKey].onAudioPlayed()
}

const onAudioEnd = (id: string, reason: 'tail' | 'current') => {
  if (repeat.value === 1) {
    const player = audioPlayer.value.find(e => e.$el.dataset.materialId === id)
    if (!player) return
    player.play()
    return
  }

  const playlistOrder = {
    tail: tailsAudioOrder.value,
    current: currentAudioOrder.value
  }[reason]

  let nextAudioId = playlistOrder[playlistOrder.indexOf(id) + 1]

  if (!nextAudioId && repeat.value === 0) nextAudioId = playlistOrder[0]
  if (!nextAudioId && playingAll.value && reason === 'current') nextAudioId = tailsAudioOrder.value[0]
  if (!nextAudioId) return

  const nextAudioPlayer = audioPlayer.value.find(e => e.$el.dataset.materialId === nextAudioId)
  if (!nextAudioPlayer) return
  nextAudioPlayer.play()
}

watch(shuffle, val => {
  if (val) {
    tailsAudioOrder.value = _shuffle(tailsAudioOrder.value)
    currentAudioOrder.value = _shuffle(currentAudioOrder.value)
    return
  }

  tailsAudioOrder.value = [...unshuffledOrders.value.tails]
  currentAudioOrder.value = [...unshuffledOrders.value.current]
})

watch(
  () => props.context,
  () => {
    currentAudioOrder.value = [...new Set(props.context.songs.filter(m => m.unit_id === unitId).map(m => m.key))]
    tailsAudioOrder.value = props.context.student.tails.map(e => tailSongKey(e)).filter((key): key is string => !!key)

    unshuffledOrders.value = {
      current: [...currentAudioOrder.value],
      tails: [...tailsAudioOrder.value]
    }
  },
  { immediate: true }
)

onMounted(async () => {
  if (!window.location.hash.startsWith('#song=')) return

  const songKey = window.location.hash.slice('#song='.length)
  window.location.hash = ''

  await nextTick()
  audioPlayer.value.find(e => e.$el.dataset.materialId === songKey)?.play()
})
</script>

<template>
  <section class="student-cabinet__page student-playlist-page student-playlist-page_blue">
    <menu v-if="unitHasSongs" class="student-playlist-page__toolbar mt-2">
      <button
        class="student-playlist-page__switch-btn student-playlist-page__switch-btn_primary"
        :class="{ 'student-playlist-page__switch-btn_active': shuffle }"
        :disabled="!tailsAudioOrder.length && !currentAudioOrder.length"
        @click="playAll"
      >
        <v-icon>mdi-play</v-icon>
      </button>

      <button
        class="student-playlist-page__switch-btn"
        :class="{ 'student-playlist-page__switch-btn_active': shuffle }"
        @click="
          () => {
            shuffle = !shuffle
          }
        "
      >
        <v-icon>mdi-shuffle-variant</v-icon>
      </button>

      <button
        class="student-playlist-page__switch-btn"
        :class="{ 'student-playlist-page__switch-btn_active': repeat > -1 }"
        @click="changeRepeat"
      >
        <v-icon v-if="repeat === 1" key="one">mdi-repeat-once</v-icon>
        <v-icon v-else key="playlist">mdi-repeat</v-icon>
      </button>

      <span class="flex-spacer" />

      <v-buttons-group v-model="mode" class="student-playlist-page__button-with-background">
        <v-button mode="primary block">
          <v-icon>mdi-account-voice</v-icon>
        </v-button>
        <v-button mode="primary block">
          <v-icon>mdi-voice-off</v-icon>
        </v-button>
      </v-buttons-group>
    </menu>

    <div v-else class="py-3 d-flex flex-column align-center justify-center">
      <v-icon class="gray-text" size="96">mdi-music-off</v-icon>

      <span class="text-title my-2">В этом юните нет песен.</span>

      <v-button mode="outlined primary filled" :href="$getLink({ tab: 'home' })" @click="$gotoOnClick">
        <v-icon size="24" class="mr-1">logo_small</v-icon>
        Главная страница
      </v-button>
    </div>

    <transition-group name="list-animation" tag="ul" class="student-materials-block__list mb-4">
      <li v-for="material in materials.current" :key="material.id" class="list-animation-item">
        <p class="text-body-bold mb-2" v-text="material.name" />
        <AudioPlayerV2
          ref="audioPlayer"
          color="blue"
          :student-id="studentId"
          :material="material"
          :data-material-id="material.id"
          @play="onAudioPlay(material.id, 'current')"
          @ended="onAudioEnd(material.id, 'current')"
        />
      </li>
    </transition-group>

    <section class="student-materials-block">
      <header class="student-materials-block__header">
        <v-icon size="40" class="blue-text">mdi-playlist-music</v-icon>

        <div class="d-flex flex-column flex-spacer mx-2">
          <h2 class="text-title-bold">Хвосты</h2>
          <p v-if="tailsAudioOrder.length" class="gray-text text-caption">
            {{ pluralize(tailsAudioOrder.length, 'аудиоурок', 'аудиоурока', 'аудиоуроков') }}
          </p>
        </div>

        <img class="student-materials-block__header-image" :src="asset('img/student-playlist-hero.png')" height="260" />
      </header>

      <ul v-if="!materials.tails.length" class="student-materials-block__list">
        <li class="d-flex align-center text-title-bold green-text px-d-3 pa-1">
          <v-icon size="48" class="mr-2">like-green</v-icon>
          Молодец, хвостов по песням нет!
        </li>
      </ul>

      <transition-group v-else name="list-animation" tag="ul" class="student-materials-block__list">
        <li v-for="material in materials.tails" :key="material.id" class="list-animation-item mx-2 mt-2">
          <p class="text-body-bold mb-2" v-text="material.name" />
          <AudioPlayerV2
            ref="audioPlayer"
            color="blue"
            :student-id="studentId"
            :material="material"
            :data-material-id="material.id"
            @play="onAudioPlay(material.id, 'tail')"
            @ended="onAudioEnd(material.id, 'tail')"
          />
        </li>
      </transition-group>
    </section>

    <div v-if="context.student.teachers.length">
      <Cinderella v-if="context.smartbook.scene === 'parrot'" :context="context" />
      <Molly v-if="context.smartbook.scene === 'monkey'" :context="context" />
      <Magician v-if="context.smartbook.scene === 'magician'" :context="context" />
      <Snowwhite v-if="context.smartbook.scene === 'snowwhite'" :context="context" />
    </div>
  </section>
</template>

<style lang="scss">
@use 'playlist-styles.scss' as *;
</style>
